import type { ReactNode } from "react";

interface PageHeaderProps {
  title: string;
  subtitle?: string | ReactNode;
  description?: string | ReactNode;
  badge?: ReactNode;
  actions?: ReactNode;
  className?: string;
  contentClassName?: string;
  titleClassName?: string;
  media?: ReactNode;
  children?: ReactNode;
}

/**
 * Standard page header with title, optional subtitle, and right-aligned action area.
 */
const headerClasses =
  "grid grid-cols-1 items-start gap-2 sm:gap-3 md:grid-cols-[minmax(0,1fr)_auto]";
const contentClassesBase =
  "flex flex-col gap-3 sm:gap-4";
const titleRowClasses =
  "flex flex-wrap items-center gap-2";
const subtitleClasses = "text-base text-slate-500";
const descriptionClasses = "text-sm text-slate-500";
const actionsClasses =
  "flex flex-wrap items-center gap-2 sm:justify-end";

export function PageHeader({
  title,
  subtitle,
  description,
  badge,
  actions,
  className,
  contentClassName,
  titleClassName,
  media,
  children,
}: PageHeaderProps) {
  const renderParagraph = (
    value: string | ReactNode | undefined,
    defaultClassName: string,
  ) => {
    if (!value) {
      return null;
    }

    if (typeof value === "string") {
      return <p className={defaultClassName}>{value}</p>;
    }

    return value;
  };

  const contentClasses = [contentClassesBase, contentClassName]
    .filter(Boolean)
    .join(" ");
  const titleClasses = ["text-4xl font-semibold", titleClassName]
    .filter(Boolean)
    .join(" ");

  return (
    <header className={[headerClasses, className].filter(Boolean).join(" ")}>
      <div className={contentClasses}>
        {media ? <div className="flex items-center gap-3">{media}</div> : null}
        <div className={titleRowClasses}>
          <h1 className={titleClasses}>{title}</h1>
          {badge}
        </div>
        {renderParagraph(subtitle, subtitleClasses)}
        {renderParagraph(description, descriptionClasses)}
        {children}
      </div>
      {actions ? <div className={actionsClasses}>{actions}</div> : null}
    </header>
  );
}
